"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { parseUnits } from "viem";
import { useAccount } from "wagmi";
import { z } from "zod";
import { ResponsiveModal } from "@/components/features/staking/ResponsiveModal";
import { Button } from "@/components/ui/button";
import { useCastStatementVote } from "@/hooks/statement/useCastStatementVote";
import { useStatementVoteBudget } from "@/hooks/statement/useStatementVoteBudget";
import { AmountSection } from "./vote/AmountSection";
import { SourcePicker } from "./vote/SourcePicker";

// Minimum vote threshold: 0.001 DNM (18 decimals)
const MIN_DNM_WEI = parseUnits("0.001", 18);

function isUserRejectionError(err: unknown): boolean {
  return (
    err instanceof Error && err.message.toLowerCase().includes("user rejected")
  );
}

/**
 * Build the statement vote zod schema with i18n messages.
 * Uses the `statement` namespace (not `validation`) because the plan specifies
 * `vote.minAmount` / `vote.invalidAmount` keys from the statement namespace.
 */
function makeStatementVoteSchema(
  t: ReturnType<typeof useTranslations<"statement">>,
) {
  return z.object({
    source: z.enum(["wallet", "stake"]).optional(),
    amount: z
      .string()
      .min(1, { message: t("vote.minAmount") })
      .refine((v) => /^\d+(\.\d{1,18})?$/.test(v), {
        message: t("vote.invalidAmount"),
      })
      .refine(
        (v) => {
          try {
            return parseUnits(v, 18) > 0n;
          } catch {
            return false;
          }
        },
        { message: t("vote.minAmount") },
      )
      .refine(
        (v) => {
          try {
            return parseUnits(v, 18) >= MIN_DNM_WEI;
          } catch {
            return false;
          }
        },
        { message: t("vote.minAmount") },
      ),
  });
}

type VoteFormValues = z.infer<ReturnType<typeof makeStatementVoteSchema>>;

interface StatementVoteModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  statementId: string;
  index: number;
  hasVoted: boolean;
}

export const StatementVoteModal: React.FC<StatementVoteModalProps> = ({
  open,
  onOpenChange,
  statementId,
  index,
  hasVoted,
}) => {
  const t = useTranslations("statement");
  const { address, isConnected } = useAccount();

  const budget = useStatementVoteBudget(statementId, open && isConnected);
  const cast = useCastStatementVote(statementId, index);

  const schema = useMemo(() => makeStatementVoteSchema(t), [t]);

  const form = useForm<VoteFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { source: undefined, amount: "" },
    mode: "onChange",
  });

  const reset = form.reset;
  useEffect(() => {
    if (open) reset({ source: undefined, amount: "" });
  }, [open, reset]);

  const source = form.watch("source");
  const amountStr = form.watch("amount");

  // Compute max for current source
  const max =
    source === "wallet"
      ? budget.freeAvailable
      : source === "stake"
        ? budget.stakedAvailable
        : 0n;

  // Compute whether amount exceeds the max (inline validation beyond zod)
  let amountWei = 0n;
  let amountExceedsMax = false;
  if (source !== undefined && amountStr) {
    try {
      amountWei = parseUnits(amountStr, 18);
      amountExceedsMax = amountWei > max;
    } catch {
      amountExceedsMax = false;
    }
  }

  // Additional cross-field validation: amount > max
  const amountError = amountExceedsMax
    ? source === "wallet"
      ? t("vote.exceedsWallet")
      : t("vote.exceedsStaked")
    : undefined;

  const canSubmit =
    source !== undefined &&
    !budget.isLoading &&
    amountWei > 0n &&
    amountWei >= MIN_DNM_WEI &&
    amountWei <= max &&
    !budget.isError &&
    !amountExceedsMax &&
    form.formState.isValid;

  const onVote = async (support: "yes" | "no") => {
    if (!address || source === undefined || !canSubmit) return;
    try {
      await cast.castAsync(
        support,
        source,
        parseUnits(amountStr, 18).toString(),
        address,
      );
      onOpenChange(false);
    } catch (err) {
      if (!isUserRejectionError(err)) {
        toast.error(t("vote.error"));
      }
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("vote.title")}
      desktopMaxWidth="lg"
    >
      <FormProvider {...form}>
        <div className="flex flex-col gap-4 pt-2">
          {/* Gasless note */}
          <p className="text-sm text-text-secondary">
            {t("vote.snapshotNote")}
          </p>

          {hasVoted && (
            <p className="text-sm text-text-secondary">{t("vote.againHint")}</p>
          )}

          {/* Budget returns note */}
          <p className="text-sm text-text-secondary">
            {t("vote.budgetReturnsNote")}
          </p>

          {/* Source picker — wallet first (physical right in RTL) */}
          <SourcePicker
            walletBalance={budget.freeAvailable}
            stakedAvailable={budget.stakedAvailable}
            selected={source}
            onSelect={(s) =>
              form.setValue("source", s, { shouldValidate: true })
            }
            isError={budget.isError}
          />

          {/* Amount input */}
          <AmountSection
            maxAmount={max}
            sourceSelected={source !== undefined}
            isError={budget.isError}
          />

          {/* Cross-field over-budget error — always rendered to keep layout stable */}
          <p className="min-h-4 text-xs text-start text-semantic-error">
            {amountError ?? " "}
          </p>

          {/* YES / NO submit buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="solid"
              colors="primary"
              className="flex-1"
              loading={cast.isPending}
              disabled={!canSubmit || cast.isPending}
              onClick={() => onVote("yes")}
            >
              {t("vote.yes")}
            </Button>
            <Button
              type="button"
              variant="bordered"
              colors="default"
              className="flex-1"
              loading={cast.isPending}
              disabled={!canSubmit || cast.isPending}
              onClick={() => onVote("no")}
            >
              {t("vote.no")}
            </Button>
          </div>
        </div>
      </FormProvider>
    </ResponsiveModal>
  );
};
